import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useCreateCustomer } from "@/features/customers/hooks/use-customers";
import type { Customer } from "@/features/customers/types";
import { PlusIcon } from "@phosphor-icons/react";
import { CustomerForm } from "./customer-form";
import { useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { useTranslation } from "@/features/utils/i18n";

export const CreateCustomerDialog = () => {
  const { t } = useTranslation("customers");
  const { company: clientId } = useParams<{ company: string }>();
  const [open, setOpen] = useState(false);
  const [customerData, setCustomerData] = useState<Partial<Customer>>({
    addresses: {
      email: [],
      phone: [],
      postal: [],
    },
    organization: {},
  });

  const createCustomer = useCreateCustomer();

  const handleChange = (updates: Partial<Customer>) => {
    setCustomerData((prev) => ({ ...prev, ...updates }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!clientId) return;

    try {
      // Generate display_name from given_name and family_name if not set
      const display_name =
        customerData.display_name ||
        `${customerData.given_name || ""} ${customerData.family_name || ""}`.trim();

      await createCustomer.mutateAsync({
        clientId,
        data: {
          ...customerData,
          display_name,
        },
      });

      toast.success(t("messages.createSuccess"));
      setOpen(false);
      setCustomerData({
        addresses: {
          email: [],
          phone: [],
          postal: [],
        },
        organization: {},
      });
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : t("messages.createError"),
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <PlusIcon className="h-4 w-4 mr-2" />
          {t("actions.add")}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{t("dialog.createTitle")}</DialogTitle>
            <DialogDescription>
              {t("dialog.createDescription")}
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="max-h-[calc(90vh-200px)] py-4">
            <div className="pr-4">
              <CustomerForm
                customer={customerData as Customer}
                onChange={handleChange}
                mode="create"
              />
            </div>
          </ScrollArea>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
            >
              {t("actions.cancel")}
            </Button>
            <Button type="submit" disabled={createCustomer.isPending}>
              {createCustomer.isPending
                ? t("dialog.creating")
                : t("actions.create")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
